import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDocument, useCollection, addDocument, updateDocument, deleteDocument, where } from '@/hooks/useFirestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowLeft, 
  Plus, 
  Monitor, 
  Edit, 
  Trash2, 
  Power,
  Settings,
  Image as ImageIcon,
  ExternalLink,
  Copy
} from 'lucide-react';

const ScreenForm = ({ screen, locationId, onSave, onClose }) => {
  const [formData, setFormData] = useState(screen || {
    name: '',
    orientation: 'landscape',
    resolution: '1920x1080',
    status: 'offline',
    locationId
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave({ ...formData, locationId });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="screenName">Screen Name *</Label>
        <Input
          id="screenName"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Menu Board 1"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="orientation">Orientation</Label>
          <Select 
            value={formData.orientation} 
            onValueChange={(value) => setFormData({ ...formData, orientation: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="landscape">Landscape</SelectItem>
              <SelectItem value="portrait">Portrait</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="resolution">Resolution</Label>
          <Select 
            value={formData.resolution} 
            onValueChange={(value) => setFormData({ ...formData, resolution: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1920x1080">1920x1080 (Full HD)</SelectItem>
              <SelectItem value="3840x2160">3840x2160 (4K)</SelectItem>
              <SelectItem value="1280x720">1280x720 (HD)</SelectItem>
              <SelectItem value="1080x1920">1080x1920 (Portrait FHD)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : (screen ? 'Update' : 'Create')} Screen
        </Button>
      </DialogFooter>
    </form>
  );
};

const LocationDetail = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { isHQ } = useAuth();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);

  const { document: location, loading: locationLoading } = useDocument('locations', locationId);
  const { documents: screens } = useCollection('screens', [where('locationId', '==', locationId)]);
  const { documents: media } = useCollection('media');
  const { documents: playlists } = useCollection('playlists');

  const handleSaveScreen = async (data) => {
    if (editingScreen) {
      await updateDocument('screens', editingScreen.id, data);
      toast({ title: 'Screen updated successfully' });
    } else {
      await addDocument('screens', data);
      toast({ title: 'Screen created successfully' });
    }
    setEditingScreen(null);
  };

  const handleDeleteScreen = async (screenId) => {
    await deleteDocument('screens', screenId);
    toast({ title: 'Screen deleted successfully' });
  };

  const handleToggleScreen = async (screen) => {
    const newStatus = screen.status === 'online' ? 'offline' : 'online';
    await updateDocument('screens', screen.id, { status: newStatus });
    toast({ title: `Screen ${newStatus === 'online' ? 'enabled' : 'disabled'}` });
  };

  const copyPlayerUrl = (screenId) => {
    const url = `${window.location.origin}/player/${screenId}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Player URL copied to clipboard' });
  };

  if (locationLoading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>;
  }

  if (!location) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-slate-400">Location not found</p>
        <Button variant="link" onClick={() => navigate('/locations')}>Go back to locations</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/locations')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">{location.name}</h1>
          <p className="text-slate-400">{location.city} • {location.address}</p>
        </div>
        <Badge 
          className={`${location.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-600 text-slate-300'}`}
        >
          {location.status || 'active'}
        </Badge>
      </div>

      {/* Location Info Card */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Location Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-400">Region</p>
              <p className="text-white">{location.region || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Contact Email</p>
              <p className="text-white">{location.contactEmail || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Contact Phone</p>
              <p className="text-white">{location.contactPhone || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Screens</p>
              <p className="text-white">{screens.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Screens Section */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Screens</h2>
        {isHQ && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingScreen(null)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Screen
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle>{editingScreen ? 'Edit' : 'Add New'} Screen</DialogTitle>
                <DialogDescription>Configure the screen settings</DialogDescription>
              </DialogHeader>
              <ScreenForm 
                screen={editingScreen} 
                locationId={locationId}
                onSave={handleSaveScreen} 
                onClose={() => { setDialogOpen(false); setEditingScreen(null); }}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {screens.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Monitor className="w-12 h-12 text-slate-500 mb-4" />
            <p className="text-slate-400">No screens configured</p>
            {isHQ && <p className="text-sm text-slate-500">Click "Add Screen" to add a display screen</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {screens.map(screen => {
            const screenPlaylist = playlists.find(p => p.id === screen.playlistId);
            const playlistMedia = screenPlaylist?.mediaIds?.map(id => media.find(m => m.id === id)).filter(Boolean) || [];
            
            return (
              <Card key={screen.id} className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${screen.status === 'online' ? 'bg-green-500/20' : 'bg-slate-700'}`}>
                        <Monitor className={`w-5 h-5 ${screen.status === 'online' ? 'text-green-400' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <CardTitle className="text-white text-base">{screen.name}</CardTitle>
                        <CardDescription>{screen.resolution} • {screen.orientation}</CardDescription>
                      </div>
                    </div>
                    <Badge 
                      className={`${screen.status === 'online' ? 'bg-green-500/20 text-green-400' : screen.status === 'maintenance' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}
                    >
                      {screen.status || 'offline'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Playlist Preview */}
                  <div className="mb-4 p-3 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                      <ImageIcon className="w-4 h-4" />
                      <span>Playlist: {screenPlaylist?.name || 'No playlist assigned'}</span>
                    </div>
                    {playlistMedia.length > 0 && (
                      <div className="flex gap-1">
                        {playlistMedia.slice(0, 4).map((m, i) => (
                          <div key={i} className="w-12 h-8 bg-slate-800 rounded overflow-hidden">
                            {m.type === 'image' ? (
                              <img src={m.url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-xs text-slate-500">Video</div>
                            )}
                          </div>
                        ))}
                        {playlistMedia.length > 4 && (
                          <div className="w-12 h-8 bg-slate-800 rounded flex items-center justify-center text-xs text-slate-400">
                            +{playlistMedia.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => window.open(`/player/${screen.id}`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyPlayerUrl(screen.id)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    {isHQ && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => { setEditingScreen(screen); setDialogOpen(true); }}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className={screen.status === 'online' ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}
                          onClick={() => handleToggleScreen(screen)}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-slate-800 border-slate-700">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Screen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{screen.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteScreen(screen.id)} className="bg-red-600 hover:bg-red-700">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LocationDetail;
